import { ArrowRight, CheckCircle, Star, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGoogleReviews } from "@/hooks/useGoogleReviews";

const prePainted = typeof window !== "undefined" && window.__HERO_PREPAINTED__;
const anim = (cls) => (prePainted ? "" : ` ${cls}`);

export const HeroSection = () => {
  const { rating, user_ratings_total } = useGoogleReviews();
  const ratingStr = (rating ?? 4.9).toFixed(1).replace(".", ",");
  const countLabel = user_ratings_total > 0 ? `${user_ratings_total} avis Google` : "Avis Google";

  const scrollToDevis = () => {
    const element = document.querySelector("#devis");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const features = [
    "Devis gratuit en 1h",
    "Équipe professionnelle",
    "Prix compétitifs",
  ];

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background - Clean gradient without distracting image */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        {/* Decorative shapes */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#FF6B00] rounded-full opacity-20 blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#FF6B00] rounded-full opacity-10 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white">
            {/* Badge */}
            <div className={`inline-flex items-center gap-2 bg-[#FF6B00] px-4 py-2 rounded-full mb-6${anim("animate-fade-in")}`} data-testid="hero-top-rating">
              <Star className="w-4 h-4 text-white fill-white" />
              <span className="text-sm font-bold">Note {ratingStr}/5 - Plus de 500 déménagements par an</span>
            </div>

            {/* Title */}
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6${anim("animate-slide-up")}`}>
              Déménagement Particuliers et Entreprises à Paris, en Île-de-France et partout en France
            </h1>

            {/* Subtitle */}
            <p className={`text-lg sm:text-xl text-white/90 mb-8 max-w-xl${anim("animate-slide-up stagger-1")}`}>
              TRANSFAST DÉMÉNAGEMENT accompagne particuliers et professionnels avec plus de 500 déménagements réalisés chaque année. Devis gratuit sous 1 heure, équipes professionnelles et prestations sur mesure.
            </p>

            {/* Features */}
            <div className={`flex flex-wrap gap-4 mb-8${anim("animate-slide-up stagger-2")}`}>
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg"
                >
                  <CheckCircle className="w-4 h-4 text-[#FF6B00]" />
                  <span className="text-sm font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons - Same height and style */}
            <div className={`flex flex-col sm:flex-row gap-4${anim("animate-slide-up stagger-3")}`}>
              <Button
                onClick={scrollToDevis}
                className="bg-[#FF6B00] hover:bg-[#e55f00] text-white font-bold uppercase tracking-wide h-14 px-8 text-base shadow-xl hover:shadow-2xl group flex items-center justify-center"
                data-testid="hero-cta-devis"
              >
                Obtenir mon devis gratuit
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <a
                href="https://wa.me/33783408585?text=Bonjour%2C%20je%20souhaite%20obtenir%20un%20devis%20pour%20mon%20d%C3%A9m%C3%A9nagement."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] hover:bg-[#1da851] text-white font-bold uppercase tracking-wide h-14 px-8 rounded-md text-base shadow-xl hover:shadow-2xl flex items-center justify-center gap-2"
                data-testid="hero-cta-whatsapp"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Contact WhatsApp
              </a>
            </div>
          </div>

          {/* Right Content - Clean truck image display */}
          <div className={`mt-8 lg:mt-0${anim("animate-fade-in stagger-2")}`}>
            <div className="relative">
              {/* Main image container - clean rounded design */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/20 bg-white">
                <picture>
                  <source srcSet="/assets/transfast/hero-truck-mobile.webp" media="(max-width: 768px)" type="image/webp" />
                  <source srcSet="/assets/transfast/hero-truck.webp" type="image/webp" />
                  <img
                    src="/assets/transfast/hero-truck-opt.jpg"
                    alt="Camion TRANSFAST DÉMÉNAGEMENT devant la Tour Eiffel - D&#233;m&#233;nagement Paris &#206;le-de-France"
                    className="w-full h-auto object-cover"
                    loading="eager"
                    fetchPriority="high"
                    width="600"
                    height="400"
                    decoding="async"
                  />
                </picture>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl" data-testid="hero-google-badge">
                <div className="flex items-center gap-3">
                  <div className="bg-[#FF6B00] p-2 rounded-lg">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-extrabold text-[#004E98]">{ratingStr}/5</p>
                    <p className="text-sm text-slate-500">{countLabel}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
